import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaChevronDown, FaLinkedinIn, FaYoutube, FaClock } from 'react-icons/fa';
import styles from './Contact.module.scss';
import { useState, useEffect } from 'react';
import ContactForm from '../../components/ContactForm';
import { HERO, RESPONSE_TIME, CONTACT_INFO, FAQS, MAP_SECTION, FAQ_SECTION, FORM_SECTION, INFO_SECTION } from '../../content/contact';
import { PAGE_SEO, SOCIAL_LINKS } from '../../content/site';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';

const CONTACT_ICONS = {
  email: FaEnvelope,
  phone: FaPhone,
  address: FaMapMarkerAlt,
  hours: FaClock,
} as const;

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger the animation when component mounts
    setIsVisible(true);
  }, []);

  // scroll action to suggest scrolling more
  const handleScrollHintClick = () => {
    window.scrollTo({
      top: window.innerHeight - 80,
      behavior: 'smooth'
    });
  }

  return (
    <div className={styles.contactPage}>
      <Seo {...seoPropsFor(PAGE_SEO.contact)} />

      <div className={isVisible ? 'main-content' : ''}>
        {/* Hero Section */}
        <section className={styles.contactHero}>
          <div className={styles.container}>
            <h1>{HERO.title}</h1>
            <p>{HERO.subtitle}</p>
          </div>
          <button
            type="button"
            className={styles.scrollHint}
            onClick={handleScrollHintClick}
            aria-label="Scroll down"
          >
            <FaChevronDown />
            <span>Scroll</span>
          </button>
        </section>

        {/* Contact Content */}
        <section className={styles.contactContent}>
          <div className={styles.container}>
            <div className={styles.contactGrid}>
              {/* Contact Form */}
              <div className={styles.contactForm}>
                <h2>{FORM_SECTION.heading}</h2>
                <p className={styles.formDescription}>{RESPONSE_TIME.formDescription}</p>
                <ContactForm />
              </div>

              {/* Contact Info */}
              <div className={styles.contactInfo}>
                <h2>{INFO_SECTION.heading}</h2>
                <p className={styles.infoDescription}>{INFO_SECTION.description}</p>

                <div className={styles.infoCards}>
                  {CONTACT_INFO.map((info, index) => {
                    const Icon = CONTACT_ICONS[info.iconKey];
                    return (
                      <div className={styles.infoCard} key={index}>
                        <div className={styles.infoIconContainer}>
                          <Icon className={styles.infoIcon} />
                        </div>
                        <div className={styles.infoContent}>
                          <h3>{info.title}</h3>
                          <p className={styles.infoDescription}>{info.description}</p>
                          {info.link ? (
                            <a href={info.link} className={styles.infoLink}>
                              {info.lines.map((line, i) => (
                                <span key={i}>
                                  {line}
                                  {i < info.lines.length - 1 && <br />}
                                </span>
                              ))}
                            </a>
                          ) : (
                            <p className={styles.infoText}>
                              {info.lines.map((line, i) => (
                                <span key={i}>
                                  {line}
                                  {i < info.lines.length - 1 && <br />}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Response Promise */}
                <div className={styles.responsePromise}>
                  <h3>{RESPONSE_TIME.promiseHeading}</h3>
                  <p>{RESPONSE_TIME.promiseBody}</p>
                </div>

                {/* Social Links */}
                <div className={styles.socialLinks}>
                  <h3>{INFO_SECTION.connectHeading}</h3>
                  <div className={styles.socialIcons}>
                    <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                      <FaLinkedinIn />
                    </a>
                    <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer">
                      <FaYoutube />
                    </a>
                  </div>
                  <p className={styles.youtubeText}>
                    {INFO_SECTION.youtubeText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className={styles.mapSection}>
          <div className={styles.container}>
            <h2>{MAP_SECTION.heading}</h2>
            <div className={styles.locationContainer}>
              <div className={styles.map}>
                <iframe
                  src={MAP_SECTION.mapEmbedSrc}
                  title={MAP_SECTION.mapTitle}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className={styles.locationVideo}>
                <iframe
                  src={MAP_SECTION.videoEmbedSrc}
                  title={MAP_SECTION.videoTitle}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className={styles.faqSection}>
          <div className={styles.container}>
            <div className={styles.faqHeader}>
              <h2>{FAQ_SECTION.heading}</h2>
              <p>{FAQ_SECTION.subtitle}</p>
            </div>

            <div className={styles.faqGrid}>
              {FAQS.map((faq, index) => (
                <div className={styles.faqCard} key={index}>
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>


    </div>
  );
};

export default ContactPage;
